import { GTM } from '@/constants/GtmConstants'
import { adminGuard, customerGuardV2 } from '@/router/guards'
import TheAccountPage from '@/views/TheAccountPage.vue'
import TheAllManufacturersPage from '@/views/TheAllManufacturersPage.vue'
import TheAuthenticationPage from '@/views/TheAuthenticationPage.vue'
import TheCartPage from '@/views/TheCartPage.vue'
import TheCategoryPage from '@/views/TheCategoryPage.vue'
import TheCheckoutPage from '@/views/TheCheckoutPage.vue'
import TheCmsPage from '@/views/TheCmsPage.vue'
import TheCollectionPage from '@/views/TheCollectionPage.vue'
import TheErrorPage from '@/views/TheErrorPage.vue'
import TheManufacturerPage from '@/views/TheManufacturerPage.vue'
import TheOpcPage from '@/views/TheOpcPage.vue'
import TheProductDetailPage from '@/views/TheProductDetailPage.vue'
import TheSearchPage from '@/views/TheSearchPage.vue'
import TheSuccessPage from '@/views/TheSuccessPage.vue'
import TheWishListPage from '@/views/TheWishListPage.vue'
import { createRouter, createWebHistory } from 'vue-router'

export const FALLBACK_ROUTE_NAME = 'fallback'

const routes = [
  // static routes
  { path: '/', name: 'home', component: TheCmsPage },
  {
    path: '/error',
    name: 'error',
    component: TheErrorPage
  },
  {
    path: '/checkout/cart',
    name: 'cart',
    component: TheCartPage
  },
  {
    path: '/search',
    name: 'search',
    component: TheSearchPage
  },
  {
    path: '/merkliste',
    name: 'wishlist',
    component: TheWishListPage,
    beforeEnter: customerGuardV2
  },
  {
    path: '/checkout/v2/success/:h',
    name: 'checkout_success',
    component: TheSuccessPage
  },
  // dynamic routes
  {
    path: '/auth/v2/:action',
    name: 'authAction',
    component: TheAuthenticationPage
  },
  {
    path: '/:prefix*-pdp-p:pId(\\d+)-c:cId(\\d+):suffix?',
    name: 'pdp',
    component: TheProductDetailPage
  },
  {
    path: '/:prefix*-opc-p:pId(\\d+)-c:cId(\\d+):suffix?',
    name: 'opc',
    component: TheOpcPage
  },
  {
    path: '/:prefix*-man-m:id(\\d+):suffix?',
    name: 'manufacturer',
    component: TheManufacturerPage
  },
  {
    path: '/winery-a-z',
    name: 'winery_a_z',
    component: TheAllManufacturersPage
  },
  {
    path: '/:prefix*-col-c:id(\\d+):suffix?',
    name: 'collection',
    component: TheCollectionPage,
    beforeEnter: adminGuard
  },
  {
    path: '/:prefix*-cat-c:id(\\d+):suffix?',
    name: 'category',
    component: TheCategoryPage
  },
  {
    path: '/:prefix*-cms-p:id(\\d+):suffix?',
    name: 'cms',
    component: TheCmsPage
  },
  {
    path: '/checkout/v2',
    name: 'checkout_default',
    component: TheCheckoutPage,
    beforeEnter: customerGuardV2
  },
  // Address routes
  {
    path: '/checkout/v2/address',
    props: true,
    name: 'checkout_address',
    component: TheCheckoutPage,
    beforeEnter: customerGuardV2
  },
  {
    path: '/checkout/v2/address/:type',
    props: true,
    name: 'checkout_address_type',
    component: TheCheckoutPage,
    beforeEnter: customerGuardV2
  },
  {
    path: '/checkout/v2/address/:type/:action',
    props: true,
    name: 'checkout_address_action',
    component: TheCheckoutPage,
    beforeEnter: customerGuardV2
  },
  // Payment routes
  {
    path: '/checkout/v2/payment',
    props: true,
    name: 'checkout_payment',
    component: TheCheckoutPage,
    beforeEnter: customerGuardV2
  },
  {
    path: '/checkout/v2/payment/:action',
    props: true,
    name: 'checkout_payment_action',
    component: TheCheckoutPage,
    beforeEnter: customerGuardV2
  },
  // Delivery route
  {
    path: '/checkout/v2/delivery',
    props: true,
    name: 'checkout_delivery',
    component: TheCheckoutPage,
    beforeEnter: customerGuardV2
  },
  // Review route
  {
    path: '/checkout/v2/review',
    props: true,
    name: 'checkout_review',
    component: TheCheckoutPage,
    beforeEnter: customerGuardV2
  },
  // Guest checkout routes
  {
    path: '/checkout/guest',
    name: 'checkout_guest_default',
    component: TheCheckoutPage
  },
  // Guest address routes
  {
    path: '/checkout/guest/address',
    props: true,
    name: 'checkout_guest_address',
    component: TheCheckoutPage
  },
  {
    path: '/checkout/guest/address/:type',
    props: true,
    name: 'checkout_guest_address_type',
    component: TheCheckoutPage
  },
  {
    path: '/checkout/guest/address/:type/:action',
    props: true,
    name: 'checkout_guest_address_action',
    component: TheCheckoutPage
  },
  // Guest payment routes
  {
    path: '/checkout/guest/payment',
    props: true,
    name: 'checkout_guest_payment',
    component: TheCheckoutPage
  },
  {
    path: '/checkout/guest/payment/:action',
    props: true,
    name: 'checkout_guest_payment_action',
    component: TheCheckoutPage
  },
  // Guest delivery route
  {
    path: '/checkout/guest/delivery',
    props: true,
    name: 'checkout_guest_delivery',
    component: TheCheckoutPage
  },
  // Guest review route
  {
    path: '/checkout/guest/review',
    props: true,
    name: 'checkout_guest_review',
    component: TheCheckoutPage
  },
  // Account management routes (admin only)
  {
    path: '/account/manage/v2',
    name: 'account_default',
    component: TheAccountPage,
    beforeEnter: adminGuard
  },
  {
    path: '/account/manage/v2/:action',
    name: 'account_action',
    component: TheAccountPage,
    beforeEnter: adminGuard,
    props: true
  },
  { path: '/:catchAll(.*)', name: FALLBACK_ROUTE_NAME, component: TheErrorPage }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes
})

router.beforeEach(
  async (
    { path: to, name: toName, query },
    { path: from, name: fromName },
    next
  ) => {
    if (fromName && from === to) {
      next()
      return
    }

    if (typeof window === 'undefined') {
      console.warn('window is undefined')
      return
    }

    // Check for original-customer-id parameter
    if (query['original-customer-id']) {
      const { customer } = await _getModules()
      await customer.waitUntilPending()

      // Wait for profile data to be loaded (with timeout)
      let attempts = 0
      const maxAttempts = 20 // 2 seconds max wait
      while (
        attempts < maxAttempts &&
        !customer.profile &&
        !customer.profileV2
      ) {
        await new Promise((resolve) => setTimeout(resolve, 100))
        attempts++
      }

      // If profile data still isn't loaded after waiting, show error banner to be safe
      if (attempts >= maxAttempts && !customer.profile && !customer.profileV2) {
        customer.setAdminPermissionError(true)
        const newQuery = { ...query }
        delete newQuery['original-customer-id']
        next({ path: to, query: newQuery })
        return
      }

      if (customer.customerIsAdmin) {
        // Admin is logged in, set the cookie and clear any existing error
        const { default: UtilityHelper } = await import(
          '@/services/helpers/UtilityHelper'
        )
        await UtilityHelper.setOriginalCustomerId(
          query['original-customer-id'],
          {
            refreshCart: true,
            reloadPage: true
          }
        )
        customer.setAdminPermissionError(false)

        // Clear the query parameter to avoid showing it in the URL
        const newQuery = { ...query }
        delete newQuery['original-customer-id']
        next({ path: to, query: newQuery })
        return
      } else {
        // Not admin, show error banner
        customer.setAdminPermissionError(true)

        // Clear the query parameter to avoid showing it in the URL
        const newQuery = { ...query }
        delete newQuery['original-customer-id']
        next({ path: to, query: newQuery })
        return
      }
    } else {
      // Check if original-customer-id cookie is set on every route change
      const { default: CookieHelper } = await import(
        '@/services/helpers/CookieHelper'
      )
      const isAdminLoggedInAsCustomer = !!CookieHelper.getCookie(
        'original-customer-id'
      )

      // Update admin permission error state based on cookie presence
      const { customer } = await _getModules()
      await customer.waitUntilPending()

      if (isAdminLoggedInAsCustomer || customer.customerIsAdmin) {
        // Clear admin permission error if admin is logged in as customer or if user is admin
        customer.setAdminPermissionError(false)
      }
    }

    if (!toName.includes('checkout') && !toName.includes('opc')) {
      const { checkout } = await _getModules()
      checkout.clearData()
    }

    // Clear guest checkout data when navigating away from guest routes
    // (either to non-checkout routes or to regular checkout routes)
    if (
      fromName &&
      fromName.includes('checkout_guest') &&
      !toName.includes('checkout_guest')
    ) {
      const { guestCheckout } = await _getModules()
      if (guestCheckout) {
        guestCheckout.clearData()
      }
    }

    void _refreshData()
    void _setupDataLayer(toName)

    next()
  }
)

router.afterEach(async (to, from) => {
  const { WALKER_COMMANDS, elb, DomHelper, customer, features } =
    await _getModules()

  if (typeof window === 'undefined') {
    console.warn('window is undefined')
    return
  }

  if (from.path !== to.path && !to.path.includes('-opc-')) {
    DomHelper.scrollTo('#root')
  }

  await customer.establishSession()

  // Wait for profile data to be ready
  const waitForProfile = async () => {
    const maxAttempts = 10
    let attempts = 0
    while (!customer.customerProfile?.customer_id && attempts < maxAttempts) {
      await new Promise((resolve) => setTimeout(resolve, 500))
      attempts++
    }
  }

  await waitForProfile()

  const elbData = {
    customerId: customer.customerProfile.customer_id,
    completedOrders: customer.customerProfile.completed_orders_count,
    lastOrderDaysAgo: customer.customerProfile.days_since_last_order
  }

  const { getVwoExperimentsForElbwalker } = await import(
    '@/services/helpers/VwoExperimentsHelper'
  )
  const vwoData = getVwoExperimentsForElbwalker()
  Object.assign(elbData, vwoData)
  Object.assign(elbData, features.gatekeeperForElbwalker)

  elb(WALKER_COMMANDS.run, { user: elbData })
})

const _refreshData = async () => {
  const { wishlist, customer, lazyStore } = await _getModules()
  await customer.establishSession()
  await lazyStore.setForceLoad({ value: false })

  void wishlist.refreshWishlistItems()
  void wishlist.refreshWishlistSoldOutItems()
}

const _setupDataLayer = async (module) => {
  const { TagManager } = await _getModules()
  TagManager.pageUpdate(module, GTM[module])
}

const _getModules = (() => {
  let cache

  return async () => {
    if (cache) return cache

    const [
      { useNewCheckoutStore },
      { useCustomerStore },
      { useWishlistStore },
      { useLazyStore },
      { useGuestCheckoutStore },
      { useFeaturesStore },
      { default: TagManager },
      { elb },
      { GLOBAL_STORE },
      { WALKER_COMMANDS },
      { default: DomHelper }
    ] = await Promise.all([
      await import('@/stores/newCheckout.js'),
      await import('@/stores/customer'),
      await import('@/stores/wishlist'),
      await import('@/stores/lazyStore'),
      await import('@/stores/guestCheckout'),
      await import('@/stores/features'),
      await import('@/services/analytics/TagManager'),
      await import('@elbwalker/walker.js'),
      await import('@/constants/GlobalConstants'),
      await import('@/services/helpers/ElbwalkerHelper'),
      await import('@/services/helpers/DomHelper')
    ])
    cache = {
      checkout: useNewCheckoutStore(),
      customer: useCustomerStore(),
      wishlist: useWishlistStore(),
      lazyStore: useLazyStore(),
      guestCheckout: useGuestCheckoutStore(),
      features: useFeaturesStore(),
      TagManager,
      elb,
      GLOBAL_STORE,
      WALKER_COMMANDS,
      DomHelper
    }
    return cache
  }
})()

export default router
